import React from 'react';
import { Tag, Tooltip } from 'antd';
import type { Studio2Session } from '../live/types';

type Props = {
  session: Studio2Session;
  // Cohort (trainer) view only — omitted in the per-player (self) view, which
  // has no roster presence to report.
  onlineCount?: number;
  totalCount?: number;
};

// --- "last computed" status formatters -----------------------------------
// computed_at is a real wall-clock instant → shown as an absolute local
// datetime. Historical sessions additionally show the as-of scenario market
// DATE (UTC); real-time sessions omit it (it coincides with computed_at).
const absoluteLocal = (iso?: string | null): string => {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const asOfLabel = (
  iso: string | null | undefined,
  isHistorical: boolean,
): string => {
  if (!iso) return '';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  const d = new Date(ms);
  return isHistorical
    ? // Historical: the real market date (UTC, matching the scenario-date convention).
      d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : // Real-time: the wall-clock datetime, browser-local.
      d.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const TopBar: React.FC<Props> = ({ session, onlineCount, totalCount }) => {
  const showOnline = onlineCount != null && totalCount != null;
  const computedAt = session.analysis_computed_at;
  const isHistorical = session.scenario_type === 1; // 1 = historical, 0 = real-time
  return (
    <div className="studio2__topbar">
      <div className="studio2__brand">
        <Tooltip title={session.name}>
          <span className="studio2__brand-title">{session.name}</span>
        </Tooltip>
        <span className="studio2__brand-meta">
          {showOnline && (
            <>
              <span className="studio2__dot studio2__dot--online" />
              {onlineCount}/{totalCount} online
              <span>·</span>
            </>
          )}
          <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
            {session.scenario_label}
          </Tag>
          <span>·</span>
          {session.interval_label}
          <span>·</span>
          {session.reporting_currency}
        </span>
      </div>
      {computedAt && (
        <span className="studio2__topbar-status">
          {isHistorical ? (
            <>
              Last computed on {absoluteLocal(computedAt)} for historical date{' '}
              {asOfLabel(session.analysis_computed_for_datetime, true)}
            </>
          ) : (
            <>Last computed on {absoluteLocal(computedAt)}</>
          )}
        </span>
      )}
    </div>
  );
};

export default TopBar;
